import { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import QuestionnaireInfoBar from '../questionnaireInfoBar';
import ReportAnswerGrid from '../reportAnswerGrid';
import ReportTextReplies from '../reportTextReplies';
import UsersFilter from '../attributeReviewReport/usersFilter';
import RankingResults from './rankingResults';
import {
  prepareAnswerGridResults,
  prepareTextReplyResults,
  prepareQuestionnaireRankingResults,
} from '../../../utility/reportUtils';
import { customSearch } from '../../../utility/uiUtils';
import { isArrayEmpty, trimString } from '../../../utility/helpers';
import { ATTRIBUTES } from '../../../constants/attributes';
import { QUESTIONNAIRE_QUESTION_TYPES } from '../../../constants/survey';

const useStyles = makeStyles(({ spacing }) => ({
  content: {
    paddingBottom: spacing(7),
  },
  answerGrid: {
    marginTop: spacing(6),
  },
}));

const QuestionnaireReviewReport = ({
  translations,
  questionnaire,
  selectedUsers = [],
  textReplyQuestionId = null,
  isTeamReview,
  isCompleted,
  reviewersCount = 0,
  hasCompletedCount = false,
  onUserSelect,
  onClearSelectedUsers,
  onViewTextReply,
}) => {
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState('');
  const {
    name,
    finishedCount,
    questions = [],
    participants = [],
    selectAnswers = {},
    textAnswers = {},
    rankingAnswers = {},
  } = questionnaire;
  const isSingleParticipant = participants?.length === 1;
  const hasFilter = useMemo(() => participants?.length !== 1, [participants]);
  const users = customSearch(participants || [], trimString(searchValue), true);
  const shouldFilterResults = !isSingleParticipant;
  const filteredSelectResults = useMemo(
    () => prepareAnswerGridResults(selectAnswers, selectedUsers),
    [selectAnswers, selectedUsers]
  );
  const filteredTextResults = useMemo(
    () =>
      prepareTextReplyResults(textAnswers, selectedUsers, shouldFilterResults),
    [textAnswers, selectedUsers, shouldFilterResults]
  );
  const filteredRankingResults = useMemo(
    () =>
      prepareQuestionnaireRankingResults(
        rankingAnswers,
        selectedUsers,
        shouldFilterResults
      ),
    [rankingAnswers, selectedUsers, shouldFilterResults]
  );

  const handleUserSelect = useCallback(
    (selectedUserId, isSelected) => {
      onUserSelect(selectedUserId, isSelected);
    },
    [onUserSelect]
  );

  useEffect(() => {
    const [participant] = participants || [];
    if (
      isSingleParticipant &&
      participant?.finished &&
      isArrayEmpty(selectedUsers)
    ) {
      handleUserSelect(participant.id);
    }
  }, [isSingleParticipant, participants, selectedUsers, handleUserSelect]);

  const handleSearch = searchTerm => {
    setSearchValue(searchTerm);
  };

  const handleClearSelection = () => {
    onClearSelectedUsers();
    setSearchValue('');
  };

  const renderSelectQuestion = question => {
    const options = question.options || [];

    return (
      <ReportAnswerGrid
        key={`questionnaire_select_${question.id}`}
        className={classes.answerGrid}
        translations={translations}
        hasPercentageView={!isSingleParticipant}
        totalAnswers={options.length}
        rows={[{ ...question, answers: options }]}
        attributeLayout={ATTRIBUTES.verticalLayout}
        results={filteredSelectResults}
        hideTitle
      />
    );
  };

  const renderOpenTextQuestion = question => (
    <ReportTextReplies
      key={`questionnaire_open_text_${question.id}`}
      className={classes.answerGrid}
      questions={[question]}
      results={filteredTextResults}
      selectedQuestionId={textReplyQuestionId}
      onExpand={onViewTextReply}
    />
  );

  const renderRankingQuestion = question => (
    <RankingResults
      key={`questionnaire_ranking_${question.id}`}
      question={question}
      options={question.options}
      results={filteredRankingResults?.[question.id] || []}
      selectedQuestionId={textReplyQuestionId}
      onExpand={onViewTextReply}
    />
  );

  const renderQuestion = question => {
    if (
      question.type === QUESTIONNAIRE_QUESTION_TYPES.SINGLE_SELECT ||
      question.type === QUESTIONNAIRE_QUESTION_TYPES.MULTI_SELECT
    ) {
      return renderSelectQuestion(question);
    }

    if (question.type === QUESTIONNAIRE_QUESTION_TYPES.OPEN_TEXT) {
      return renderOpenTextQuestion(question);
    }

    if (question.type === QUESTIONNAIRE_QUESTION_TYPES.RANKING) {
      return renderRankingQuestion(question);
    }

    return null;
  };

  return (
    <div>
      <QuestionnaireInfoBar
        translations={translations}
        name={name}
        finishedCount={finishedCount}
        reviewersCount={reviewersCount}
        hasCompletedCount={hasCompletedCount}
      />
      <div className={classes.content}>
        <UsersFilter
          translations={translations.usersFilter}
          title={translations.usersFilter.titleMatrix}
          searchTerm={searchValue}
          users={users}
          activeColors={[]}
          selectedUsers={selectedUsers}
          maxSelectedUsers={0}
          isTeamReview={isTeamReview}
          isSurveyCompleted={isCompleted}
          onClear={handleClearSelection}
          onSearch={handleSearch}
          onSelect={handleUserSelect}
          hasReportChart={false}
          hasAverageChart={false}
          hasFilter={hasFilter}
          shouldEnableOnlyFinishedUsers
          isUserClearable
          hasSearch
        />
        {questions.map(renderQuestion)}
      </div>
    </div>
  );
};

QuestionnaireReviewReport.propTypes = {
  translations: PropTypes.object.isRequired,
  questionnaire: PropTypes.shape({}).isRequired,
  selectedUsers: PropTypes.arrayOf(PropTypes.number),
  textReplyQuestionId: PropTypes.number,
  isTeamReview: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  reviewersCount: PropTypes.number,
  hasCompletedCount: PropTypes.bool,
  onUserSelect: PropTypes.func.isRequired,
  onClearSelectedUsers: PropTypes.func.isRequired,
  onViewTextReply: PropTypes.func.isRequired,
};

export default QuestionnaireReviewReport;
